import { Component, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  FeatureFlags,
  LaunchDarklyService,
  MultiMediaItem,
  MultiMediaItemRequest,
  PhotoOrder,
  PhotoOrderService,
  ToasterService,
  VideoLinkTipsConstants,
  VideoUploadTipsConstants,
  YoutubeVideoAd,
} from '@lc/core';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  MultiMediaItemForm,
  MultiMediaItemFormComponent,
  MultimediaTableComponent,
} from '@lc/shared-components-media';
import { ActivatedRoute } from '@angular/router';
import { FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Spinner, SpinnerComponent } from '@re-core/ngx-ui';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { BrandService } from '@lc/core/lib/services/brand.service';
import { E2eIdDirective } from '@lc/ui';
import { UploadVideoComponent } from '../../../../../../libs/shared-components/src/lib/media/upload-video';

class ViewModel {
  readonly multimediaLinks: MultiMediaItem[];

  constructor(readonly photoOrder: PhotoOrder) {
    this.multimediaLinks = photoOrder.media?.multimedia || [];
  }
}

@Component({
  selector: 'lc-manage-multimedia',
  templateUrl: './manage-multimedia.component.html',
  host: { class: 'p-3 d-block spinner-container' },
  imports: [
    SpinnerComponent,
    MatIcon,
    MultiMediaItemFormComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MultimediaTableComponent,
    AsyncPipe,
    E2eIdDirective,
    UploadVideoComponent,
  ],
})
export class ManageMultimediaComponent {
  private route = inject(ActivatedRoute);
  private photoOrderService = inject(PhotoOrderService);
  private readonly toasterService = inject(ToasterService);
  readonly brandService = inject(BrandService);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  addFormGroup = new MultiMediaItemForm();

  // Angular FormDirective needs to be reset after submission so that the error validations are removed.
  // See: https://github.com/angular/components/issues/4190
  @ViewChild(FormGroupDirective) formDirective: FormGroupDirective;

  readonly viewModel$: Observable<ViewModel>;
  readonly spinner = Spinner.loading('Loading...');

  readonly videoTitle: WritableSignal<string> = signal('Upload video about the listing');
  readonly videoSubTitle: WritableSignal<string> = signal(
    "We'll use this video to make the YouTube ad campaign.",
  );
  readonly videoTips: WritableSignal<readonly string[]> = signal(VideoUploadTipsConstants);
  readonly videoLinkTips: WritableSignal<readonly string[]> = signal(VideoLinkTipsConstants);
  readonly videoFeatureEnabled: Signal<boolean> = toSignal(
    this.launchDarklyService.getFeature$(FeatureFlags.UPLOAD_LISTING_VIDEO),
  );

  readonly marketingOrderId: WritableSignal<string> = signal('');
  readonly video: WritableSignal<YoutubeVideoAd | null> = signal(null);
  readonly videoSubmitted: WritableSignal<boolean> = signal(false);

  constructor() {
    // Query the photo order
    this.viewModel$ = this.route.params.pipe(
      map((params) => params.photoOrderId),
      switchMap((orderId) => this.photoOrderService.getOrder$(orderId)),
      map((order) => {
        this.marketingOrderId.set(order.marketingOrderId);

        // The backend getLinks endpoint returns:
        // - Marketing order video if it has been submitted
        // - Photo order video if uploaded but not yet submitted (only if no marketing video)
        // - Nothing if no video exists or was deleted after submission
        const video = order.media?.youtubeVideoAd;

        if (video) {
          // Check if video has been submitted:
          // - submittedAt is a Date → submitted
          // - submittedAt is null → not submitted
          const isSubmitted = video.submittedAt !== null && video.submittedAt !== undefined;

          this.videoSubmitted.set(isSubmitted);
          this.video.set(video);
        } else {
          // No video - show upload option
          this.videoSubmitted.set(false);
          this.video.set(null);
        }

        return new ViewModel(order);
      }),
      tap(() => this.spinner.stop()),
      takeUntilDestroyed(),
    );
  }

  async addLink(viewModel: ViewModel) {
    if (viewModel.multimediaLinks.map((m) => m.uri).includes(this.addFormGroup.value.uri)) {
      this.toasterService.showError('Cannot submit a duplicate link');
      return;
    }

    // prepare add link request
    const request = new MultiMediaItemRequest(this.addFormGroup.value);
    request.createdById = viewModel.photoOrder.mediaOwnerId;

    await this.photoOrderService.addMultimediaLink(viewModel.photoOrder, request);

    this.addFormGroup.reset();
    this.formDirective.resetForm();
  }

  async submitVideo(viewModel: ViewModel) {
    // Safety check - use the video signal instead of viewModel cache
    if (!this.video()) {
      this.toasterService.showError('No video to submit');
      return;
    }

    try {
      // Call service to submit video (transfer from photoOrder to marketingOrder)
      const updatedPhotoOrder = await this.photoOrderService.submitVideo(viewModel.photoOrder);

      this.toasterService.showInfo('Video submitted successfully');

      // Update video to show the marketing order version
      if (updatedPhotoOrder.media?.youtubeVideoAd) {
        this.video.set(updatedPhotoOrder.media.youtubeVideoAd);
        this.videoSubmitted.set(true);
      }
    } catch (error) {
      // Display the error message from the backend if available
      // Try different possible error message locations
      const errorMessage = error?.error?.error?.message
        || error?.error?.message
        || error?.error
        || error?.message
        || 'Failed to submit video. Please try again or contact support.';
      // If errorMessage is an object, try to stringify it
      const displayMessage = typeof errorMessage === 'string'
        ? errorMessage
        : 'Failed to submit video. Please try again or contact support.';
      this.toasterService.showError(displayMessage);
    }
  }
}
